/**
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

// One-line definice závislostí pro snazší merge.
// U vybraných modulů je konzole dovolena.
/* eslint-disable max-len, no-console */

// Toto je jediný skript, který by měl přistupovat k neobvyklým globálním proměnným.
// Pokud jsou ve zbytku aplikace potřeba jejich data, tak by jim měla být předána v konstruktoru.
/* global process */

import { vueIntegration } from '@sentry/vue';
import DsLocalStorageAdapter from 'js-data-localstorage';
import isNil from 'lodash/isNil';
import Vue from 'vue';

import config from '../config';
import DevicePreview from '../device/device-preview';
import DeviceStateful from '../device/device-stateful';
import Player from '../player/player';
import AnalyticsSender from './analytics/analytics-sender';
import AnalyticsStore from './analytics/analytics-store';
import AnalyticsTracker from './analytics/analytics-tracker';
import AnalyticsTrackerNoop from './analytics/analytics-tracker-noop';
import AnalyticsTransporter from './analytics/analytics-transporter';
import AudienceMultiplierResolver from './audience-multiplicator/audience-multiplier-resolver';
import Cleaner from './cleaner/cleaner';
import ConsoleLogger from './console/console-logger';
import ContentSettingsStore from './content/content-settings-store';
import ContentControllerFactory from './content/controller/content-controller-factory';
import ContentDownloader from './content/downloader/content-downloader';
import EnvUtil from './env-util';
import FingerprintFallback from './fingerprint/fingerprint-fallback';
import FingerprintHardware, {
  parseFingerprintStrategy
} from './fingerprint/fingerprint-hardware';
import FingerprintHasher from './fingerprint/fingerprint-hasher';
import FingerprintBrowser from './fingerprint/fingerprint-uuid';
import MediumExternalSourceFactory from './medium-external/medium-external-source-factory';
import PlaybackControlsEmitter from './playback-controls/playback-controls-emitter';
import ReportManager from './report/report-manager';
import SentryLogger from './sentry/sentry-logger';
import SentryOfflineIntegration from './sentry/sentry-offline-integration';
import ServerConnectionMonitor from './server/server-connection-monitor';
import { alwaysEnsureSession } from './session/session';
import Store from './store/store';
import VariablesNamespaceRegistry from './variables/variables-namespace-registry';

/**
 * @import Bottle from 'bottlejs'
 * @import { Logger } from './logger/logger'
 * @import { UpdateUrlConfig } from './update-url-config'
 * @import { Session } from './session/session'
 * @import { FingerprintStrategy } from './fingerprint'
 */

// TODO #555: Definice globálního objektu v src/build.js.
const kiwi =
  /**
   * @type {typeof window & { kiwi: {
   *   buildId: string | null | undefined,
   *   sentryDsn: string | null | undefined,
   * }}}
   */
  (window).kiwi;

/** @type {FingerprintStrategy} */
const defaultFingerprintStrategy = 'snmac';

// TODO: Lepší pojmenování a umístění souboru?
/**
 * Skoro statická třída (až na bottle, aby nemusel být předávaný jako parametr
 * do každé funkce) se společnými Bottle funkcemi pro main soubory.
 */
export default class MainCommon {
  /**
   * @param {Bottle} bottle
   */
  constructor(bottle) {
    this.bottle = bottle;
  }

  /**
   * Asynchronně vytvoří a zaregistruje Session.
   * @requires Logger
   * @requires Store
   * @returns {Promise<Session>}
   */
  async asyncEagerRegisterSession() {
    const { Logger: logger, Store: store } = this.bottle.container;
    const session = await alwaysEnsureSession(logger, store);
    const { deviceId, serverUrl, version } = session;
    // Přidání brzkých informací ze session do loggeru.
    logger.setInfo({
      deviceId,
      serverUrl,
      version
    });
    // prettier-ignore
    this.bottle
      .constant('Session', session)
      .constant('serverUrl', serverUrl);
    return session;
  }

  /**
   * Asynchronně zaregistruje Device implementaci buďto DevicePreview nebo
   * DeviceStateful na základě update URL získané z AppManager.
   * @requires updateUrlConfig
   * @returns {this}
   */
  registerDevice() {
    const { updateUrlConfig } = this.bottle.container;

    // eslint-disable-next-line no-negated-condition
    if (updateUrlConfig.preview !== undefined) {
      // Vytvoření náhledovací implementace.
      // prettier-ignore
      this.bottle
        .constant('updateUrlConfig', updateUrlConfig)
        .service('AnalyticsTracker', AnalyticsTrackerNoop)
        .service('Device', DevicePreview, 'ContentDownloader', 'Logger', 'Replacer', 'Store', 'UnixTimestamp', 'updateUrlConfig', 'VariablesNamespaceRegistry', 'ViewerManager', 'ViewerRegistry');
    } else {
      // prettier-ignore
      this.bottle
        .service('AnalyticsSender', AnalyticsSender, 'AnalyticsStore', 'AnalyticsTransporter')
        .service('AnalyticsStore', AnalyticsStore, 'Session', 'Store')
        .service('AnalyticsTracker', AnalyticsTracker, 'AnalyticsStore')
        .service('AnalyticsTransporter', AnalyticsTransporter)
        .service('Device', DeviceStateful, 'AnalyticsSender', 'AnalyticsStore', 'AnalyticsTracker', 'AnalyticsTransporter', 'AppManager', 'BasePathResolver', 'Cleaner', 'Communicator', 'ContentSettingsStore', 'ContentDownloader', 'FileSystemManager', 'FileSystemUnitFinder', 'FirmwareManager', 'Logger', 'LogicalUnitMapper', 'Replacer', 'ReplacerPower', 'ReportManager', 'Session', 'StartupManager', 'StatusInfoDialogController', 'Store', 'UnixTimestamp', 'Updater', 'VariablesNamespaceRegistry', 'ViewerManager', 'ViewerRegistry');
    }
    return this;
  }

  /**
   * Asynchronně zaregistruje FingerprintManager a předá strategii generování
   * otisku na základě URL získané z AppManager.
   * @requires updateUrlConfig
   * @returns {this}
   */
  registerFingerprintManager() {
    const updateUrlConfig = /** @type {UpdateUrlConfig} */ (
      this.bottle.container.updateUrlConfig
    );
    /** @type {FingerprintStrategy | undefined} */
    let fingerprintStrategy = defaultFingerprintStrategy;
    if (updateUrlConfig.fingerprintStrategy) {
      fingerprintStrategy =
        parseFingerprintStrategy(updateUrlConfig.fingerprintStrategy) ??
        defaultFingerprintStrategy;
    }
    // prettier-ignore
    this.bottle
      .constant('fingerprintStrategy', fingerprintStrategy)
      .service('FingerprintBrowser', FingerprintBrowser, 'FingerprintHasher')
      .service('FingerprintFallback', FingerprintFallback, 'FingerprintBrowser', 'FingerprintHardware')
      .service('FingerprintHardware', FingerprintHardware, 'fingerprintStrategy', 'FirmwareManager', 'FingerprintHasher', 'Logger', 'NetworkManager')
      .service('FingerprintHasher', FingerprintHasher);
    return this;
  }

  /**
   * @requires AppManager
   * @returns {Promise<this>}
   */
  async asyncRegisterUpdateUrlConfig() {
    const { AppManager: appManager } = this.bottle.container;
    const info = await appManager.getUpdateUrlOpaqueInfo();
    const updateUrlConfig = /** @type {UpdateUrlConfig} TODO #555 */ (
      appManager.parseUpdateUrlConfig(info)
    );
    // prettier-ignore
    this.bottle.constant('updateUrlConfig', updateUrlConfig);
    return this;
  }

  /**
   * Asynchronně zaregistruje Player.
   * @returns {this}
   */
  registerPlayer() {
    // prettier-ignore
    this.bottle.service('Player', Player, 'AppManager', 'AssetDownloader', 'BrowserCapabilities', 'Communicator', 'ContentDownloader', 'ContentSettingsStore', 'ControlManager', 'Device', 'FileSystemManager', 'FingerprintFallback', 'FocusManager', 'Logger', 'LoggerManager', 'LogicalUnitMapper', 'NetworkListener', 'PlaybackControlsEmitter', 'ReportManager', 'ScreenManager', 'ServerConnectionMonitor', 'Session', 'StatusInfoDialogController', 'Store', 'updateUrlConfig', 'VariablesNamespaceRegistry', 'ViewerManager', 'ViewerRegistry', 'VirtualKeyboardManager', 'VueFactory');
    return this;
  }

  bootstrap() {
    return (
      this
        // Používá se i jinde než v Logger.
        .registerBuildId()
        // Inicializace loggeru, aby šlo zaznamenat chyby co nejdříve
        // (kromě platformy tizen-sssp-4, kde je potřeba speciální ošetření).
        // Requires: buildId
        .registerLogger()
        .registerStore()
    );
  }

  registerBuildId() {
    this.bottle.constant('buildId', kiwi.buildId);
    return this;
  }

  /**
   * Zaregistruje společné věci pro všechny platformy.
   * @returns {this}
   */
  registerCommon() {
    // prettier-ignore
    this.bottle
      .constant('UseStore', ContentSettingsStore.createUseStore())
      .service('AudienceMultiplierResolver', AudienceMultiplierResolver)
      .service('Cleaner', Cleaner, 'BasePathResolver', 'FileSystemManager', 'Logger', 'LogicalUnitMapper', 'Store')
      .service('ContentControllerFactory', ContentControllerFactory, 'BrowserCapabilities', 'ContentDownloader', 'Logger', 'MediumExternalSourceFactory', 'PlaybackControlsEmitter', 'PreloadStrategy', 'Store', 'UnixTimestamp', 'VariablesNamespaceRegistry', 'ViewerRegistry')
      .service('ContentDownloader', ContentDownloader, 'AssetDownloader', 'BasePathResolver', 'Cleaner', 'Config', 'FileSystemManager', 'FileSystemUnitFinder', 'Logger', 'LogicalUnitMapper', 'Store', 'Session', 'ViewerRegistry')
      .service('ContentSettingsStore', ContentSettingsStore, 'UseStore')
      .service('MediumExternalSourceFactory', MediumExternalSourceFactory, 'AudienceMultiplierResolver', 'VariablesNamespaceRegistry')
      .service('PlaybackControlsEmitter', PlaybackControlsEmitter)
      .service('ReportManager', ReportManager, 'AppManager', 'buildId', 'ControlManager', 'FileSystemManager', 'FirmwareManager', 'Logger', 'MemoryManager', 'NetworkManager', 'PowerManager', 'ScreenManager', 'SensorManager', 'Session', 'SoundManager', 'TimeManager', 'VariablesNamespaceRegistry', 'ViewerRegistry')
      .service('ServerConnectionMonitor', ServerConnectionMonitor, 'serverUrl')
      .service('VariablesNamespaceRegistry', VariablesNamespaceRegistry);
    return this;
  }

  /**
   * Inicializace loggeru, aby šlo zaznamenat chyby co nejdříve.
   * @requires buildId
   * @returns {this}
   */
  registerLogger() {
    this.bottle.serviceFactory('Logger', () => {
      /** @type {Logger} */
      let logger;
      if (isNil(kiwi.sentryDsn)) {
        logger = new ConsoleLogger();
      } else {
        // TODO: import('@sentry/integrations').Offline je nějaký oficiální plugin,
        // tak případně  na něj přejít z našeho custom pluginu a vymyslet migraci dat.
        const offlineIntegration = new SentryOfflineIntegration({
          key: `${config.name}/sentryEvent`,
          maxEvents: config.sentryMaxEvents
        });
        logger = new SentryLogger(
          config.sentryCaptureErrorLevel,
          kiwi.sentryDsn,
          offlineIntegration,
          vueIntegration({ Vue })
        );
        offlineIntegration.sendEvents();
      }
      logger.setInfo({
        buildId:
          /** @type {typeof kiwi.buildId} */
          (this.bottle.container.buildId) ?? undefined,
        envEnum: EnvUtil.parse(process.env.NODE_ENV)
      });

      // TODO: Pro účely ladění nebo detekce programátorských chyb (assertů)
      // potřebuje přístup k loggeru bez nutnosti tranzitivně předávat závislosti.
      setGlobalLogger(logger);

      return logger;
    });
    return this;
  }

  registerStore() {
    this.bottle.serviceFactory(
      'Store',
      () =>
        new Store({
          storage: new DsLocalStorageAdapter({ basePath: config.name })
        })
    );
    return this;
  }

  async start() {
    // Registrace
    await this.asyncRegisterUpdateUrlConfig();
    // Registrace objektu pro generování otisku zařízení.
    this.registerFingerprintManager();
    // Registraci Device je potřeba dělat až po registraci AppManager.
    this.registerDevice();
    // Registrace playeru.
    this.registerPlayer();
    // Spuštění playeru.
    await /** @type {Player} */ (this.bottle.container.Player).start('#player');
  }
}

/**
 * Případně získá globální instanci loggeru.
 * @deprecated Používat jen opravdu výjimečně pro účely ladění/assertů.
 * @example
 * getGlobalLogger()?.error(new Error('message'));
 * getGlobalLogger()?.error(new Error('message', { cause: err }));
 * @returns {Logger | undefined}
 */
export function getGlobalLogger() {
  // @ts-expect-error
  // eslint-disable-next-line no-underscore-dangle
  return window._logger;
}

/**
 * @deprecated Používat jen v main.unit.js.
 * @param {Logger} logger
 */
export function setGlobalLogger(logger) {
  // @ts-expect-error
  // eslint-disable-next-line no-underscore-dangle
  window._logger = logger;
}
